import {useEffect} from 'react';
import {UWM_INIT_EXTERNAL_LINKS, UWM_INIT_MARKET_LOCALE, UWM_REFRESH_BADGES_DATA} from '@/consts/eventTypes';
import useEventListener from '@/hooks/useEventListener';
import useWindowEventListener from '@/hooks/useWindowListener';
import {proceedAppInit, proceedBadgesDataRefresh} from '@/services/appService';
import eventMessagesService from '@/services/events/eventMessagesService';
import {addSsoLink} from '@/state/slices/globalSlice';
import {dispatch} from '@/state/store';

type EventDetail = {
    command?: string;
    payload?: Record<string, unknown>;
};

type ExternalLinkResult = {
    link?: string;
    expirationDate?: number;
    status?: 'loading' | 'success' | 'failed';
};

type ExternalLinkEntry = {
    template?: string;
    externalLinkResult?: ExternalLinkResult;
};

const handleInitExternalLinks = (payload: Record<string, unknown> | undefined) => {
    const externalLinks = payload?.externalLinks as ExternalLinkEntry[] | undefined;
    if (!externalLinks?.length) return;

    externalLinks.forEach((entry) => {
        const {template, externalLinkResult} = entry ?? {};
        if (!template || !externalLinkResult) return;
        dispatch(
            addSsoLink({
                template,
                link: externalLinkResult.link,
                expirationDate: externalLinkResult.expirationDate,
                status: externalLinkResult.status ?? 'success',
            })
        );
    });
};

const useEvents = () => {
    const init = async (detail: EventDetail | null | undefined) => {
        const {command, payload} = detail ?? {};
        if (command === UWM_INIT_MARKET_LOCALE) {
            await proceedAppInit(payload as Parameters<typeof proceedAppInit>[0]);
            return;
        }
        if (command === UWM_INIT_EXTERNAL_LINKS) {
            handleInitExternalLinks(payload);
            return;
        }
        if (command === UWM_REFRESH_BADGES_DATA) {
            proceedBadgesDataRefresh(payload as Parameters<typeof proceedBadgesDataRefresh>[0]);
            return;
        }
    };

    useEffect(() => {
        eventMessagesService.sendLoadedMessageToParent();
    }, []);

    useWindowEventListener<MessageEvent>('message', (event) => {
        init(event.data.detail);
    });

    useEventListener<CustomEvent<EventDetail>>('mockCommand', (event) => {
        init(event.detail);
    });
};

export default useEvents;
